import React, { useState, useEffect } from 'react';
import { User, Bell, Shield, Palette, Globe, Volume2, Eye, Save, Lock, Cookie } from 'lucide-react';
import { useTranslation } from '../hooks/useTranslation';
import { CookieSettings } from './CookieSettings';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { updateUserProfile } from '../lib/supabase';
import { toast } from 'react-hot-toast';

export const Settings: React.FC = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const { user, refreshUser } = useAuth();
  const [activeSection, setActiveSection] = useState<'account' | 'notifications' | 'privacy' | 'preferences' | 'cookies'>('account');
  const [settings, setSettings] = useState({
    // Account settings
    fullName: '',
    email: '',
    bio: '',
    timezone: 'America/New_York',
    
    // Notification settings
    emailNotifications: true,
    pushNotifications: true,
    dailyReminders: true,
    manifestationUpdates: true,
    libraryUpdates: false,
    
    // Privacy settings
    profileVisibility: 'private',
    manifestationSharing: false,
    dataCollection: true,
    analytics: true,
    
    // App preferences
    theme: 'light',
    language: 'en',
    autoSave: true,
    soundEffects: true,
    vibration: true,
    dailyGoal: 3,
  });

  const [hasChanges, setHasChanges] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Load user data when component mounts
  useEffect(() => {
    if (user) {
      const userWithProfile = user as any; // Type assertion since getCurrentUser adds profile
      setSettings(prev => ({
        ...prev,
        fullName: userWithProfile.user_metadata?.full_name || '',
        email: userWithProfile.email || '',
        bio: userWithProfile.profile?.bio || '',
        timezone: userWithProfile.profile?.timezone || 'America/New_York',
        // Load notification preferences from profile if available
        ...(userWithProfile.profile?.notification_preferences ? {
          emailNotifications: userWithProfile.profile.notification_preferences.email,
          pushNotifications: userWithProfile.profile.notification_preferences.push,
          dailyReminders: userWithProfile.profile.notification_preferences.daily_reminders,
          manifestationUpdates: userWithProfile.profile.notification_preferences.manifestation_updates || false,
          libraryUpdates: userWithProfile.profile.notification_preferences.library_updates || false
        } : {}),
        // Load app preferences from profile if available
        ...(userWithProfile.profile?.app_preferences ? {
          theme: userWithProfile.profile.app_preferences.theme,
          language: userWithProfile.profile.app_preferences.language,
          autoSave: userWithProfile.profile.app_preferences.autoSave,
          soundEffects: userWithProfile.profile.app_preferences.soundEffects,
          vibration: userWithProfile.profile.app_preferences.vibration,
          dailyGoal: userWithProfile.profile.app_preferences.dailyGoal
        } : {}),
        // Load privacy preferences from profile if available
        ...(userWithProfile.profile?.privacy_preferences ? {
          profileVisibility: userWithProfile.profile.privacy_preferences.profileVisibility,
          manifestationSharing: userWithProfile.profile.privacy_preferences.manifestationSharing,
          dataCollection: userWithProfile.profile.privacy_preferences.dataCollection,
          analytics: userWithProfile.profile.privacy_preferences.analytics
        } : {})
      }));
    }
  }, [user]);

  // Check for tab in location state
  useEffect(() => {
    if (location.state && location.state.activeTab) {
      const tabName = location.state.activeTab;
      switch (tabName) {
        case 'account':
        case 'notifications':
        case 'privacy':
        case 'preferences':
        case 'cookies':
          setActiveSection(tabName);
          break;
        default:
          // If invalid tab name, default to account
          setActiveSection('account');
      }
      
      // Clear the state after using it
      window.history.replaceState({}, document.title);
    }
  }, [location]);

  const handleSettingChange = (key: string, value: any) => {
    setSettings(prev => ({ ...prev, [key]: value }));
    setHasChanges(true);
  };

  const saveSettings = async () => {
    if (!user) {
      console.log('No user found, cannot save settings');
      return;
    }
    
    console.log('Starting to save settings...', { userId: user.id, settings });
    setIsSaving(true);
    try {
      // Prepare profile data
      const profileData = {
        full_name: settings.fullName,
        bio: settings.bio,
        timezone: settings.timezone,
        notification_preferences: {
          email: settings.emailNotifications,
          push: settings.pushNotifications,
          daily_reminders: settings.dailyReminders,
          manifestation_updates: settings.manifestationUpdates,
          library_updates: settings.libraryUpdates
        },
        app_preferences: {
          theme: settings.theme,
          language: settings.language,
          autoSave: settings.autoSave,
          soundEffects: settings.soundEffects,
          vibration: settings.vibration,
          dailyGoal: settings.dailyGoal
        },
        privacy_preferences: {
          profileVisibility: settings.profileVisibility,
          manifestationSharing: settings.manifestationSharing,
          dataCollection: settings.dataCollection,
          analytics: settings.analytics
        }
      };
      
      console.log('Profile data to save:', profileData);
      
      // Update profile in Supabase
      const result = await updateUserProfile(user.id, profileData);
      console.log('Profile update result:', result);
      
      // Refresh user data to get updated profile
      console.log('Refreshing user data...');
      await refreshUser();
      console.log('User data refreshed');
      
      setHasChanges(false);
      toast.success('Settings saved successfully');
      console.log('Settings saved successfully');
    } catch (error) {
      console.error('Error saving settings:', error);
      toast.error('Failed to save settings. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const sections = [
    { id: 'account', label: t('settings.account'), icon: User },
    { id: 'notifications', label: t('settings.notifications'), icon: Bell },
    { id: 'privacy', label: t('settings.privacy'), icon: Shield },
    { id: 'preferences', label: t('settings.preferences'), icon: Palette },
    { id: 'cookies', label: t('settings.cookies'), icon: Cookie },
  ];

  return (
    <div className="max-w-6xl mx-auto px-4 py-8">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">{t('settings.title')}</h1>
        <p className="text-gray-600">{t('settings.description')}</p>
      </div>

      <div className="flex flex-col lg:flex-row gap-8">
        {/* Sidebar Navigation */}
        <div className="lg:w-64 flex-shrink-0">
          <nav className="bg-white rounded-lg shadow-sm p-2 space-y-1">
            {sections.map((section) => {
              const Icon = section.icon;
              return (
                <button
                  key={section.id}
                  onClick={() => setActiveSection(section.id as any)}
                  className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg font-medium transition-all duration-200 ${
                    activeSection === section.id
                      ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-md'
                      : 'text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  <Icon size={18} />
                  <span>{section.label}</span>
                </button>
              );
            })}
          </nav>
        </div>

        {/* Main Content */}
        <div className="flex-1">
          <div className="bg-white rounded-lg shadow-sm p-6">
            {/* Account Settings */}
            {activeSection === 'account' && (
              <div className="space-y-6">
                <h2 className="text-xl font-semibold text-gray-900">{t('settings.accountInformation')}</h2>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t('settings.fullName')}
                    </label>
                    <input
                      type="text"
                      value={settings.fullName}
                      onChange={(e) => handleSettingChange('fullName', e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t('settings.emailAddress')}
                    </label>
                    <input
                      type="email"
                      value={settings.email}
                      disabled
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg bg-gray-50 cursor-not-allowed"
                    />
                    <p className="text-xs text-gray-500 mt-1">Email cannot be changed</p>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('settings.bio')}
                  </label>
                  <textarea
                    value={settings.bio}
                    onChange={(e) => handleSettingChange('bio', e.target.value)}
                    rows={3}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    placeholder={t('settings.bioPlaceholder')}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('settings.timezone')}
                  </label>
                  <select
                    value={settings.timezone}
                    onChange={(e) => handleSettingChange('timezone', e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  >
                    <option value="America/New_York">Eastern Time (UTC-5)</option>
                    <option value="America/Chicago">Central Time (UTC-6)</option>
                    <option value="America/Denver">Mountain Time (UTC-7)</option>
                    <option value="America/Los_Angeles">Pacific Time (UTC-8)</option>
                    <option value="UTC">UTC</option>
                  </select>
                </div>

                <div className="pt-4 border-t border-gray-200">
                  <button className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg font-medium transition-colors duration-200">
                    {t('settings.deleteAccount')}
                  </button>
                  <p className="text-sm text-gray-500 mt-2">
                    {t('settings.deleteAccountWarning')}
                  </p>
                </div>
              </div>
            )}

            {/* Notification Settings */}
            {activeSection === 'notifications' && (
              <div className="space-y-6">
                <h2 className="text-xl font-semibold text-gray-900">{t('settings.notificationPreferences')}</h2>
                
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="font-medium text-gray-900">{t('settings.emailNotifications')}</h3>
                      <p className="text-sm text-gray-500">{t('settings.emailNotificationsDesc')}</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={settings.emailNotifications}
                        onChange={(e) => handleSettingChange('emailNotifications', e.target.checked)}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-purple-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-purple-600"></div>
                    </label>
                  </div>

                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="font-medium text-gray-900">{t('settings.pushNotifications')}</h3>
                      <p className="text-sm text-gray-500">{t('settings.pushNotificationsDesc')}</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={settings.pushNotifications}
                        onChange={(e) => handleSettingChange('pushNotifications', e.target.checked)}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-purple-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-purple-600"></div>
                    </label>
                  </div>

                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="font-medium text-gray-900">{t('settings.dailyReminders')}</h3>
                      <p className="text-sm text-gray-500">{t('settings.dailyRemindersDesc')}</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={settings.dailyReminders}
                        onChange={(e) => handleSettingChange('dailyReminders', e.target.checked)}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-purple-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-purple-600"></div>
                    </label>
                  </div>

                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="font-medium text-gray-900">{t('settings.manifestationUpdates')}</h3>
                      <p className="text-sm text-gray-500">{t('settings.manifestationUpdatesDesc')}</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={settings.manifestationUpdates}
                        onChange={(e) => handleSettingChange('manifestationUpdates', e.target.checked)}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-purple-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-purple-600"></div>
                    </label>
                  </div>

                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="font-medium text-gray-900">{t('settings.libraryUpdates')}</h3>
                      <p className="text-sm text-gray-500">{t('settings.libraryUpdatesDesc')}</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={settings.libraryUpdates}
                        onChange={(e) => handleSettingChange('libraryUpdates', e.target.checked)}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-purple-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-purple-600"></div>
                    </label>
                  </div>
                </div>
              </div>
            )}

            {/* Privacy Settings */}
            {activeSection === 'privacy' && (
              <div className="space-y-6">
                <h2 className="text-xl font-semibold text-gray-900">{t('settings.privacySettings')}</h2>
                
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t('settings.profileVisibility')}
                    </label>
                    <select
                      value={settings.profileVisibility}
                      onChange={(e) => handleSettingChange('profileVisibility', e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    >
                      <option value="public">Public</option>
                      <option value="friends">Friends Only</option>
                      <option value="private">Private</option>
                    </select>
                  </div>

                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="font-medium text-gray-900">{t('settings.manifestationSharing')}</h3>
                      <p className="text-sm text-gray-500">{t('settings.manifestationSharingDesc')}</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={settings.manifestationSharing}
                        onChange={(e) => handleSettingChange('manifestationSharing', e.target.checked)}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-purple-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-purple-600"></div>
                    </label>
                  </div>

                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="font-medium text-gray-900">{t('settings.dataCollection')}</h3>
                      <p className="text-sm text-gray-500">{t('settings.dataCollectionDesc')}</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={settings.dataCollection}
                        onChange={(e) => handleSettingChange('dataCollection', e.target.checked)}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-purple-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-purple-600"></div>
                    </label>
                  </div>

                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="font-medium text-gray-900">{t('settings.analytics')}</h3>
                      <p className="text-sm text-gray-500">{t('settings.analyticsDesc')}</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={settings.analytics}
                        onChange={(e) => handleSettingChange('analytics', e.target.checked)}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-purple-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-purple-600"></div>
                    </label>
                  </div>
                </div>
              </div>
            )}

            {/* App Preferences */}
            {activeSection === 'preferences' && (
              <div className="space-y-6">
                <h2 className="text-xl font-semibold text-gray-900">{t('settings.appPreferences')}</h2>
                
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t('settings.theme')}
                    </label>
                    <select
                      value={settings.theme}
                      onChange={(e) => handleSettingChange('theme', e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    >
                      <option value="light">Light</option>
                      <option value="dark">Dark</option>
                      <option value="auto">Auto</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t('settings.language')}
                    </label>
                    <select
                      value={settings.language}
                      onChange={(e) => handleSettingChange('language', e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    >
                      <option value="en">English</option>
                      <option value="es">Español</option>
                      <option value="fr">Français</option>
                      <option value="de">Deutsch</option>
                      <option value="it">Italiano</option>
                      <option value="pt">Português</option>
                      <option value="zh">中文</option>
                      <option value="ja">日本語</option>
                    </select>
                  </div>

                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="font-medium text-gray-900">{t('settings.autoSave')}</h3>
                      <p className="text-sm text-gray-500">{t('settings.autoSaveDesc')}</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={settings.autoSave}
                        onChange={(e) => handleSettingChange('autoSave', e.target.checked)}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-purple-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-purple-600"></div>
                    </label>
                  </div>

                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="font-medium text-gray-900">{t('settings.soundEffects')}</h3>
                      <p className="text-sm text-gray-500">{t('settings.soundEffectsDesc')}</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={settings.soundEffects}
                        onChange={(e) => handleSettingChange('soundEffects', e.target.checked)}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-purple-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-purple-600"></div>
                    </label>
                  </div>

                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="font-medium text-gray-900">{t('settings.vibration')}</h3>
                      <p className="text-sm text-gray-500">{t('settings.vibrationDesc')}</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={settings.vibration}
                        onChange={(e) => handleSettingChange('vibration', e.target.checked)}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-purple-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-purple-600"></div>
                    </label>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t('settings.dailyGoal')}
                    </label>
                    <div className="flex items-center space-x-4">
                      <input
                        type="range"
                        min="1"
                        max="10"
                        value={settings.dailyGoal}
                        onChange={(e) => handleSettingChange('dailyGoal', parseInt(e.target.value))}
                        className="flex-1 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                      />
                      <span className="text-sm text-gray-600 min-w-[120px]">
                        {settings.dailyGoal} {t('settings.manifestationsPerDay')}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Cookie Settings */}
            {activeSection === 'cookies' && (
              <CookieSettings />
            )}

            {/* Save Button */}
            {hasChanges && (
              <div className="mt-8 pt-6 border-t border-gray-200">
                <div className="flex items-center justify-between">
                  <p className="text-sm text-amber-600">
                    {t('settings.unsavedChanges')}
                  </p>
                  <button
                    onClick={saveSettings}
                    disabled={isSaving}
                    className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white px-6 py-2 rounded-lg font-medium transition-all duration-200 flex items-center space-x-2 disabled:opacity-70 disabled:cursor-not-allowed"
                  >
                    {isSaving ? (
                      <>
                        <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></div>
                        <span>Saving...</span>
                      </>
                    ) : (
                      <>
                        <Save size={16} />
                        <span>{t('settings.saveChanges')}</span>
                      </>
                    )}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};